import { __rest } from 'tslib';
import React__default from 'react';
import { CommonStyles } from './components/BaseProps.es.js';
import { SvgCss } from '@teko/ds-icon';
import { CORE_SIZE } from './constants.es.js';
import { ButtonShapeEnum } from '@teko/types/ds';
import { Body, Caption } from './components/Typography.es.js';
import { S as SizeContext } from './SizeContext-6faf6613.js';
import { R as Ripple } from './Ripple-cad0539e.js';
import { styled, css } from '@teko/styled';

const BUTTON_CONFIG = {
    height: {
        xs: CORE_SIZE.s24,
        s: CORE_SIZE.s32,
        m: CORE_SIZE.s40,
        l: CORE_SIZE.s48,
    },
    textColor: {
        primary: 'white',
        secondary: 'primary500',
        ghost: 'textPrimary',
        link: 'link500',
    },
    iconColor: {
        primary: 'white',
        secondary: 'primary500',
        ghost: 'textSecondary',
        link: 'link500',
    },
    disabled: {
        textColor: {
            primary: 'placeholder',
            secondary: 'placeholder',
            ghost: 'disable',
            link: 'disable',
        },
        iconColor: {
            primary: 'border',
            secondary: 'border',
            ghost: 'disable',
            link: 'disable',
        },
    },
};

const buttonStyleByVariant = (props) => {
    switch (props.variant) {
        case 'default': {
            return css `
        background-color: transparent;
        border: 1px solid
          ${props.disabled
                ? props.theme.colors.disable
                : props.theme.colors.border};
        ${!props.disabled
                ? `&:hover {
          border-color: ${props.theme.colors.textSecondary};
        }
        &:active {
          border-color: ${props.theme.colors.textPrimary};
          path[stroke],
          rect[stroke],
          line {
            stroke: ${props.theme.colors.textPrimary};
          }
          path:not([stroke]),
          rect:not([stroke]) {
            fill: ${props.theme.colors.textPrimary};
          }
        }`
                : ''}
      `;
        }
        case 'ghost': {
            return css `
        background-color: ${props.theme.colors.white};
        border: 1px solid
          ${props.disabled
                ? props.theme.colors.disable
                : props.theme.colors.border};
        ${!props.disabled
                ? `&:hover {
          border-color: ${props.theme.colors.textSecondary};
        }
        &:active {
          border-color: ${props.theme.colors.textPrimary};
          path[stroke],
          rect[stroke],
          line {
            stroke: ${props.theme.colors.textPrimary};
          }
          path:not([stroke]),
          rect:not([stroke]) {
            fill: ${props.theme.colors.textPrimary};
          }
        }`
                : ''}
      `;
        }
        case 'secondary': {
            return css `
        background-color: ${props.theme.colors.white};
        border: 1px solid
          ${props.disabled
                ? props.theme.colors.disable
                : props.theme.colors.primary500};
        ${!props.disabled
                ? `
          &:hover {
            border-color: ${props.theme.colors.primary600};
            div[type=body] {
              color: ${props.theme.colors.primary600};
            }
            path[stroke],
            rect[stroke],
            line {
              stroke: ${props.theme.colors.primary600};
            }
            path:not([stroke]),
            rect:not([stroke]) {
              fill: ${props.theme.colors.primary600};
            }
          }
          &:active {
            border-color: ${props.theme.colors.primary700};
            div[type=body] {
              color: ${props.theme.colors.primary700};
            }
            path[stroke],
            rect[stroke],
            line {
              stroke: ${props.theme.colors.primary700};
            }
            path:not([stroke]),
            rect:not([stroke]) {
              fill: ${props.theme.colors.primary700};
            }
          }
        `
                : ''}
      `;
        }
        case 'primary':
        default:
            return css `
        background-color: ${props.disabled
                ? props.theme.colors.disable
                : props.theme.colors.primary500};
        ${!props.disabled
                ? `
          &:hover {
            background-color: ${props.theme.colors.primary600};
          }
          &:active {
            background-color: ${props.theme.colors.primary700};
          }
        `
                : ''}
      `;
    }
};
const buttonShapeStyle = ({ theme, shape, size }) => {
    switch (shape) {
        case ButtonShapeEnum.round:
            return css `
        border-radius: ${parseFloat(BUTTON_CONFIG.height[size].slice(0, -3)) /
                2}rem
          ${parseFloat(BUTTON_CONFIG.height[size].slice(0, -3)) / 2}rem;
      `;
        case ButtonShapeEnum.circle:
            return css `
        border-radius: 50%;
      `;
        default:
            return css `
        border-radius: ${theme.borderRadius.s};
      `;
    }
};
const Button = (_a) => {
    var { children, block = false, shape, size: customizeSize, variant = 'default', prefix, suffix, disabled, onClick, className, href, type = 'button', backgroundColor } = _a, rest = __rest(_a, ["children", "block", "shape", "size", "variant", "prefix", "suffix", "disabled", "onClick", "className", "href", "type", "backgroundColor"]);
    const size = React__default.useContext(SizeContext);
    const mergeSize = customizeSize || size || 'm';
    const handleClick = (e) => {
        if (disabled) {
            e.preventDefault();
            return;
        }
        onClick && onClick(e);
    };
    if (variant === 'link') {
        return (React__default.createElement(StyledLink, { href: href, target: "_blank", rel: "noopener noreferrer", className: className, itemSpacing: "s4", color: disabled
                ? BUTTON_CONFIG.disabled.iconColor.link
                : BUTTON_CONFIG.iconColor.link, onClick: handleClick, disabled: !!disabled },
            prefix ? (React__default.createElement(React__default.Fragment, null,
                prefix,
                React__default.createElement(Spacer, null))) : undefined,
            children && typeof children === 'string' ? (mergeSize === 'm' ? (React__default.createElement(Body, { className: "button-text", level: 1, color: disabled
                    ? BUTTON_CONFIG.disabled.textColor.link
                    : BUTTON_CONFIG.textColor.link }, children)) : (React__default.createElement(Caption, { className: "button-text", level: 1, color: disabled
                    ? BUTTON_CONFIG.disabled.textColor.link
                    : BUTTON_CONFIG.textColor.link }, children))) : (children),
            suffix ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Spacer, null),
                suffix)) : undefined));
    }
    return (React__default.createElement(StyledButton, Object.assign({ height: BUTTON_CONFIG.height[mergeSize], padding: shape !== 'circle' && ['s0', 's20'], itemSpacing: "s8", color: disabled
            ? BUTTON_CONFIG.disabled.iconColor[variant]
            : BUTTON_CONFIG.iconColor[variant], backgroundColor: backgroundColor, variant: variant, disabled: !!disabled, block: block, shape: shape, size: mergeSize, onClick: handleClick, className: className, type: type }, rest),
        prefix ? (React__default.createElement(React__default.Fragment, null,
            prefix,
            React__default.createElement(Spacer, null))) : undefined,
        children && typeof children === 'string' ? (React__default.createElement(Body, { className: "button-text", level: mergeSize === 's' ? 2 : 1, color: disabled
                ? BUTTON_CONFIG.disabled.textColor[variant]
                : BUTTON_CONFIG.textColor[variant] }, children)) : (children),
        suffix ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Spacer, null),
            suffix)) : undefined,
        React__default.createElement("span", { style: { marginLeft: 0 } },
            React__default.createElement(Ripple, null))));
};
const Spacer = styled.span `
  width: 0.5rem;
`;
const StyledButton = styled.button(({ height, theme, color, block, disabled, backgroundColor, }) => {
    var _a, _b;
    return [
        CommonStyles,
        SvgCss,
        buttonShapeStyle,
        buttonStyleByVariant,
        `position: relative;
display: flex;
align-items: center;
justify-content: center;
outline: none;
min-width: ${height};
${color ? `color: ${((_a = theme === null || theme === void 0 ? void 0 : theme.colors) === null || _a === void 0 ? void 0 : _a[color]) || color};` : ''}
${block ? `width: 100%;` : ''}
cursor: ${disabled ? 'not-allowed' : 'pointer'};
${backgroundColor
            ? `background-color: ${((_b = theme === null || theme === void 0 ? void 0 : theme.colors) === null || _b === void 0 ? void 0 : _b[backgroundColor]) || backgroundColor} !important;`
            : ''}
&:focus {
  outline: none;
}
`,
    ];
});
const StyledLink = styled.a `
  ${CommonStyles}
  ${SvgCss}
  display: flex;
  align-items: center;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  ${(p) => { var _a, _b; return p.color ? `color: ${((_b = (_a = p.theme) === null || _a === void 0 ? void 0 : _a.colors) === null || _b === void 0 ? void 0 : _b[p.color]) || p.color}!important;` : ''; }}
  text-decoration: none;
  ${(p) => !p.disabled
    ? `
    &:hover {
      text-decoration: underline!important;
    }
  `
    : ''}
`;

export { Button as B };
